// Copyright © Veeam Software Group GmbH

import type { Form } from '@veeam/core';
import type {
    RESTTeamsPost,
    RestTeamsTab,
    RESTTeamsTeam,
} from 'api/rxjs';
import type {
    TeamsChannelItem,
    TeamsFileItem,
    TeamsFilesItem,
    TeamsPostItem,
    TeamsPostsItem,
    TeamsTabItem,
    TeamsTabsItem,
} from 'services/models/Teams/TeamsItem';

import { exploreSessionService } from 'services/exploreSessions';
import { checkItemsType } from './check-items-type';
import {
    RESTOperatorRestoreFilesOptionsFileVersionEnum,
    teamsChannelApi,
    teamsFileApi,
    teamsPostApi,
    teamsTabApi,
    teamsTeamApi,
} from 'api/rxjs';
import { TeamsItemType } from 'services/models/Teams/TeamsItem';

import type { TeamsRestoreForm } from '../interfaces/teams-restore-form';

export const getRestoreRequest = (formApi: Form<TeamsRestoreForm>) => {
    const formValue = formApi.getValue();
    const { vetSession } = exploreSessionService.getSessions();
    const items = formValue.items;

    switch (checkItemsType(formValue.items)) {
        case TeamsItemType.File:
        case TeamsItemType.Folder:
            return teamsFileApi.teamsFileOperatorRestore({
                teamId: (items[0] as TeamsFileItem).teamId,
                restoreSessionId: vetSession,
                action: {
                    reason: formValue.reason,
                    files: (items as TeamsFileItem[]).map(item => ({
                        ...item,
                        version: item.versionId,
                    })),
                    fileVersion: formValue.restoreOnlyLatest ?
                        RESTOperatorRestoreFilesOptionsFileVersionEnum.Last : RESTOperatorRestoreFilesOptionsFileVersionEnum.All,
                    restoreChangedItems: formValue.restoreChangedItems,
                    restoreMissingItems: formValue.restoreMissingItems,
                },
            });
        case TeamsItemType.Post:
            return teamsPostApi.teamsPostOperatorRestore({
                teamId: (items[0] as TeamsPostItem).teamId,
                restoreSessionId: vetSession,
                action: {
                    reason: formValue.reason,
                    channelId: (items[0] as TeamsPostItem).channelId,
                    posts: items as RESTTeamsPost[],
                },
            });
        case TeamsItemType.Tab:
            return teamsTabApi.teamsTabOperatorRestoreByMultipleChannels({
                teamId: (items[0] as TeamsTabItem).teamId,
                restoreSessionId: vetSession,
                action: {
                    reason: formValue.reason,
                    restoreChangedTabs: formValue.restoreChangedTabs,
                    restoreMissingTabs: formValue.restoreMissingTabs,
                    tabs: items as RestTeamsTab[],
                },
            });
        case TeamsItemType.Channel:
            return teamsChannelApi.teamsChannelOperatorRestore({
                teamId: (items[0] as TeamsChannelItem).teamId,
                restoreSessionId: vetSession,
                channelId: (items[0] as TeamsChannelItem).id,
                action: {
                    reason: formValue.reason,
                    restoreChangedItems: formValue.restoreChangedItems,
                    restoreMissingItems: formValue.restoreMissingItems,
                    restoreMembers: formValue.restoreMembers,
                },
            });
        case TeamsItemType.Tabs:
            return teamsTabApi.teamsTabOperatorRestore({
                teamId: (items[0] as TeamsTabsItem).teamId,
                restoreSessionId: vetSession,
                channelId: (items[0] as TeamsTabsItem).channelId,
                action: {
                    reason: formValue.reason,
                    restoreChangedTabs: formValue.restoreChangedTabs,
                    restoreMissingTabs: formValue.restoreMissingTabs,
                },
            });
        case TeamsItemType.Files:
            return teamsFileApi.teamsFileOperatorRestore({
                teamId: (items[0] as TeamsFilesItem).teamId,
                restoreSessionId: vetSession,
                action: {
                    reason: formValue.reason,
                    fileVersion: formValue.restoreOnlyLatest ?
                        RESTOperatorRestoreFilesOptionsFileVersionEnum.Last : RESTOperatorRestoreFilesOptionsFileVersionEnum.All,
                    restoreChangedItems: formValue.restoreChangedItems,
                    restoreMissingItems: formValue.restoreMissingItems,
                    channelId: (items[0] as TeamsFilesItem).channelId,
                },
            });
        case TeamsItemType.Posts:
            return teamsPostApi.teamsPostOperatorRestore({
                teamId: (items[0] as TeamsPostsItem).teamId,
                restoreSessionId: vetSession,
                action: {
                    reason: formValue.reason,
                    channelId: (items[0] as TeamsPostsItem).channelId,
                },
            });
        case TeamsItemType.Team:
            return teamsTeamApi.teamsTeamOperatorRestore({
                restoreSessionId: vetSession,
                action: {
                    restoreChangedItems: formValue.restoreChangedItems,
                    restoreMembers: formValue.restoreMembers,
                    restoreMissingItems: formValue.restoreMissingItems,
                    restoreSettings: formValue.restoreSettings,
                    impersonationAccountName: '',
                    teams: items as RESTTeamsTeam[],
                    reason: formValue.reason,
                },
            });
    }
};
